import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { services } from "../../data/services";

export default function RelatedServicesCarousel({ currentSlug }) {
  const viewportRef = useRef(null);
  const relatedServices = services.filter((service) => service.slug !== currentSlug);

  function scroll(direction) {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollBy({
      left: direction * Math.max(viewport.clientWidth * 0.84, 280),
      behavior: "smooth",
    });
  }

  return (
    <section className="section-space overflow-hidden bg-mist" aria-labelledby="related-services-title">
      <div className="container">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="eyebrow">Keep exploring</p>
            <h2 id="related-services-title" className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-[-.045em] text-ink sm:text-4xl">
              Another service for the next step.
            </h2>
            <p className="mt-4 text-base leading-7 text-slate">
              Browse the other ways The Bridge can help you move a project, purchase or obligation forward.
            </p>
          </div>
          <div className="flex shrink-0 gap-2" aria-label="Related services carousel controls">
            <button
              type="button"
              onClick={() => scroll(-1)}
              aria-label="Show previous services"
              className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 bg-white text-ink transition hover:border-ocean hover:text-ocean focus:outline-none focus-visible:ring-2 focus-visible:ring-ocean"
            >
              <ArrowLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              aria-label="Show more services"
              className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 bg-white text-ink transition hover:border-ocean hover:text-ocean focus:outline-none focus-visible:ring-2 focus-visible:ring-ocean"
            >
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        <div
          ref={viewportRef}
          className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          tabIndex="0"
          aria-label="Related services"
        >
          {relatedServices.map((service) => (
            <article
              key={service.slug}
              className="flex min-w-0 flex-none basis-[82vw] snap-start flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-ocean/30 hover:shadow-soft sm:basis-[calc(50%-0.625rem)] lg:basis-[calc(33.333%-0.835rem)]"
            >
              <Link to={`/services/${service.slug}`} className="flex h-full flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ocean">
                <img
                  src={service.image}
                  alt=""
                  loading="lazy"
                  className="aspect-[16/9] w-full object-cover"
                />
                <div className="flex flex-1 flex-col p-6">
                  <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-[.16em] text-ocean">
                    <span>{service.shortTitle}</span>
                    <ArrowUpRight size={17} aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 font-display text-2xl font-extrabold leading-tight tracking-[-.04em] text-ink">
                    {service.cardTitle}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-7 text-slate">
                    {service.description}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ocean">
                    View service <ArrowRight size={16} aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
